import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { usePreferences } from '@/hooks/useSettings';
import { useThemeStore } from '@/store';
import { cn } from '@/utils';

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Persisted in localStorage by the hook, so the choice survives navigation
  // (this component stays mounted across routes) and reload (it does not).
  const { collapsed, setCollapsed, toggleCollapsed } = useSidebarCollapsed();

  // Applying the `dark` class is NOT done here -- initTheme() in main.tsx owns
  // it, and the pre-paint script in index.html sets it before first render.
  // (For the record: the old useEffect here was redundant rather than the only
  // coverage. This module's barrel pulls in '@/store', which evaluates the
  // theme store, whose rehydrate applies the class on every route already.)
  //
  // What DOES belong here is seeding the session from the server preference.
  // This is the first component behind ProtectedRoute that every signed-in user
  // renders, so it is where the account-level choice can be read: without this
  // the store only ever knows what this browser's localStorage says, and a user
  // who picked 'dark' on one device still gets 'system' on the next one.
  //
  // The store latches after the first usable value, so the focus refetches and
  // the invalidation in useUpdatePreferences cannot overrule a ThemeToggle
  // click made later in the session. Deliberately no dependency on the whole
  // query object -- only the value we consume.
  const { data: preferences } = usePreferences();
  const serverTheme = preferences?.theme;

  useEffect(() => {
    useThemeStore.getState().hydrateFromServerPreference(serverTheme);
  }, [serverTheme]);

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        onExpand={() => setCollapsed(false)}
      />

      {/* The left inset has to track the rail width, or the header and page
          content keep a 16rem gutter beside a 4rem sidebar. */}
      <div
        className={cn(
          'h-full flex flex-col transition-[padding] duration-300 ease-in-out',
          collapsed ? 'lg:pl-16' : 'lg:pl-64'
        )}
      >
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
